import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { EmptyState, ErrorBox, Loading } from '../components/Feedback.js';
import { Barras, Secao } from '../components/Grafico.js';
import { PageHeader } from '../components/PageHeader.js';
import { useRetrato } from '../api/queries.js';
import { emReais, emReaisRedondo } from '../lib/dinheiro.js';

/**
 * "Sua adega em números": o retrato do que está guardado, de todas as adegas.
 *
 * Conta, não IA — sai na hora e de graça. Os números que viram decisão ganham
 * destaque: estilo que falta (nada para peixe ou brinde) e garrafas no auge, que
 * leva direto para "Beber antes".
 */
export function CellarStats() {
  const { data: retrato, isLoading, error } = useRetrato();

  if (isLoading) return <Loading />;

  return (
    <div className="space-y-5">
      <PageHeader titulo="Sua adega em números" subtitulo="Todas as adegas" voltar="/" />

      {error ? <ErrorBox erro={error} /> : null}

      {retrato && retrato.garrafas === 0 ? (
        <EmptyState
          titulo="Nada para contar ainda"
          descricao="Os números aparecem assim que houver garrafas guardadas."
          acao={
            <Link to="/adicionar" className="btn-primary">
              Adicionar garrafa
            </Link>
          }
        />
      ) : null}

      {retrato && retrato.garrafas > 0 ? (
        <>
          <div className="grid grid-cols-2 gap-2">
            <Numero icone="🍾" rotulo="Garrafas" valor={retrato.garrafas} />
            <Numero icone="🍷" rotulo="Vinhos diferentes" valor={retrato.vinhosDiferentes} />
            <Numero icone="🌎" rotulo="Países" valor={retrato.paises} />
            <Link to="/beber-primeiro" className="block rounded-2xl transition active:scale-[0.98]">
              <Numero icone="⏳" rotulo="No auge" valor={retrato.noAuge} destaque={retrato.noAuge > 0} />
            </Link>
          </div>

          {retrato.porPais.length > 0 ? (
            <Secao icone="🌎" titulo="Países">
              <Barras
                cor="bg-adega-wine-light"
                itens={retrato.porPais.map((item) => ({
                  rotulo: `${item.bandeira ? `${item.bandeira} ` : ''}${item.pais}`,
                  valor: item.garrafas,
                }))}
              />
              {retrato.semPais > 0 ? (
                <p className="text-xs text-adega-muted">
                  {retrato.semPais} {retrato.semPais === 1 ? 'garrafa' : 'garrafas'} sem país cadastrado.
                </p>
              ) : null}
            </Secao>
          ) : null}

          <Secao icone="🥂" titulo="Estilos">
            <div className="grid grid-cols-4 gap-1.5">
              <Estilo rotulo="Tintos" valor={retrato.porEstilo.tinto} />
              <Estilo rotulo="Brancos" valor={retrato.porEstilo.branco} />
              <Estilo rotulo="Rosés" valor={retrato.porEstilo.rose} />
              <Estilo rotulo="Espumantes" valor={retrato.porEstilo.espumante} />
            </div>
            {retrato.porEstilo.outros > 0 ? (
              <p className="text-xs text-adega-muted">
                + {retrato.porEstilo.outros} de outros estilos ou sem estilo cadastrado.
              </p>
            ) : null}
            {retrato.avisoEstilo ? (
              <p className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-200">
                ⚠️ {retrato.avisoEstilo}
              </p>
            ) : null}
          </Secao>

          {retrato.uvas.length > 0 ? (
            <Secao icone="🍇" titulo="Uvas mais presentes">
              <Barras
                cor="bg-violet-400"
                itens={retrato.uvas.map((item) => ({ rotulo: item.uva, valor: item.garrafas }))}
              />
              <p className="text-xs text-adega-muted">Conta também as uvas dos cortes.</p>
            </Secao>
          ) : null}

          <Secao icone="💰" titulo="Valor guardado">
            {retrato.valorGuardado ? (
              <div className="space-y-2 rounded-2xl bg-adega-surface px-4 py-3">
                <div>
                  <p className="text-2xl font-semibold">{emReaisRedondo(retrato.valorGuardado.total)}</p>
                  <p className="text-xs text-adega-muted">
                    em {retrato.valorGuardado.comPreco}{' '}
                    {retrato.valorGuardado.comPreco === 1 ? 'garrafa' : 'garrafas'} · média{' '}
                    {emReais(retrato.valorGuardado.media)}
                  </p>
                </div>
                {retrato.valorGuardado.maisCara ? (
                  <p className="truncate border-t border-adega-border pt-2 text-sm">
                    <span className="text-adega-muted">Mais cara: </span>
                    {retrato.valorGuardado.maisCara.vinho} ·{' '}
                    {emReais(retrato.valorGuardado.maisCara.valor)}
                  </p>
                ) : null}
                {/* O total nunca é apresentado como se fosse a adega inteira: sem
                    esta linha, quem preencheu 3 de 26 garrafas leria menos do que tem. */}
                {retrato.valorGuardado.semPreco > 0 ? (
                  <p className="text-xs text-adega-muted">
                    {retrato.valorGuardado.semPreco}{' '}
                    {retrato.valorGuardado.semPreco === 1 ? 'garrafa está' : 'garrafas estão'} sem valor
                    informado — o total é pelo menos isto.
                  </p>
                ) : null}
              </div>
            ) : (
              <p className="rounded-2xl bg-adega-surface px-4 py-3 text-sm text-adega-muted">
                Aparece depois que você informar o valor pago em alguma garrafa, no cadastro ou na edição.
              </p>
            )}
          </Secao>

          <Secao icone="✨" titulo="Destaques">
            <dl className="divide-y divide-adega-border rounded-xl border border-adega-border text-sm">
              {retrato.produtorComMaisGarrafas ? (
                <Destaque rotulo="Produtor com mais garrafas">
                  {retrato.produtorComMaisGarrafas.produtor} · {retrato.produtorComMaisGarrafas.garrafas}
                </Destaque>
              ) : null}
              {retrato.safraMaisAntiga ? (
                <Destaque rotulo="Safra mais antiga">
                  {retrato.safraMaisAntiga.safra} · {retrato.safraMaisAntiga.vinho}
                </Destaque>
              ) : null}
              <Destaque rotulo="Uva preferida">
                {retrato.uvaPreferida ? (
                  `${retrato.uvaPreferida.uva} · ★ ${retrato.uvaPreferida.notaMedia.toLocaleString('pt-BR')}`
                ) : (
                  <span className="text-adega-muted">após notas em "Bebi esta"</span>
                )}
              </Destaque>
            </dl>
          </Secao>
        </>
      ) : null}
    </div>
  );
}

function Numero({
  icone,
  rotulo,
  valor,
  destaque = false,
}: {
  icone: string;
  rotulo: string;
  valor: number;
  destaque?: boolean;
}) {
  return (
    <div className={`rounded-2xl bg-adega-surface px-4 py-3 ${destaque ? 'ring-1 ring-adega-gold/40' : ''}`}>
      <p className="text-xs text-adega-muted">
        <span aria-hidden>{icone}</span> {rotulo}
      </p>
      <p className={`text-2xl font-semibold ${destaque ? 'text-adega-gold' : ''}`}>{valor}</p>
    </div>
  );
}

function Estilo({ rotulo, valor }: { rotulo: string; valor: number }) {
  return (
    <div className="rounded-xl bg-adega-surface px-1 py-2 text-center">
      <p className={`text-lg font-semibold ${valor === 0 ? 'text-adega-muted' : ''}`}>{valor}</p>
      <p className="truncate text-[11px] text-adega-muted">{rotulo}</p>
    </div>
  );
}

function Destaque({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 px-3 py-2.5">
      <dt className="shrink-0 text-adega-muted">{rotulo}</dt>
      <dd className="min-w-0 truncate text-right">{children}</dd>
    </div>
  );
}
